import React, { useEffect, useState } from 'react';
import { Box, styled } from '@mui/material';
import { Header } from '../Header';
import { SystemBanner } from '../SystemBanner';
import { StripeTermsAcceptanceGate } from '../StripeTermsAcceptanceGate';
import { useLocation } from 'react-router-dom';

export type AuthenticatedLayoutProps = {
}

const ui = {
    // Matches every page's own PageContainer background, so the brief gap while a
    // route's lazy-loaded chunk resolves doesn't flash white between page navigations.
    // Aug 2026 - overflow:'clip', not 'hidden' - a real, meaningful distinction here.
    // `overflow: hidden` only blocks USER-driven scrolling (wheel/drag) - an element with it can
    // still have its scrollTop changed PROGRAMMATICALLY (e.g. the browser's own focus-triggered
    // scrollIntoView, which is exactly what Stripe's cross-origin PaymentElement iframe fires when
    // a field inside it is focused). `overflow: clip` forbids ALL scrolling of this box, including
    // that programmatic path - see the matching html/body fix below for the full explanation.
    Layout: styled(Box)(({ theme }) => ({
        height: '100vh',
        width: '100vw',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'clip',
        backgroundColor: '#F0F4F8',
    })),
};

export const AuthenticatedLayout: React.FC<React.PropsWithChildren<AuthenticatedLayoutProps>> = (props) => {
    const { children } = props;
    const location = useLocation();
    const [currentUrl, setUrl] = useState(location.pathname);

    useEffect(() => {
        setUrl(location.pathname);
    }, [location.pathname]);

    // Aug 2026 - same reasoning as ui.Layout's overflow:'clip' above, belt-and-suspenders on
    // html/body themselves: a cross-origin iframe (Stripe's PaymentElement) focusing an input
    // fires the browser's own focus-triggered scrollIntoView, which walks the ancestor chain and
    // can still adjust scrollTop on an 'overflow: hidden' box - 'hidden' only blocks USER-driven
    // scrolling, not this programmatic path. 'clip' (not 'hidden') is what actually forbids it.
    // Scoped to whenever an authenticated page is mounted (not a global theme change), so public/
    // sign-in pages that render outside this layout are untouched.
    useEffect(() => {
        const html = document.documentElement;
        const previousHtmlOverflow = html.style.overflow;
        const previousBodyOverflow = document.body.style.overflow;
        html.style.overflow = 'clip';
        document.body.style.overflow = 'clip';
        return () => {
            html.style.overflow = previousHtmlOverflow;
            document.body.style.overflow = previousBodyOverflow;
        };
    }, []);

    return (
        <ui.Layout>
            <StripeTermsAcceptanceGate />
            <SystemBanner />
            <Header currentUrl={currentUrl} />
            {children}
        </ui.Layout>
    );
}